import axios from 'axios';

// Root cause of a real production incident (2026-08-13): this previously
// fell back to a hardcoded, unrelated legacy Render.com deployment for any
// non-localhost hostname. Because REACT_APP_API_BASE_URL was never set at
// build time, every production build silently pointed the ENTIRE frontend
// at that stale backend instead of the real one serving the page — some
// long-stable routes (e.g. /clients) happened to still work there, which
// made the misdirection easy to miss, while newer routes (e.g.
// /integrations/readiness) returned old/incompatible responses and were
// misread as failures. Defaulting to same-origin means whatever host is
// actually serving this bundle is always the host it talks to — no
// build-time env var can be forgotten and silently break this again.
// REACT_APP_API_BASE_URL remains available as an explicit override for
// local development against a separately-hosted API.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:5000/api'
    : `${window.location.origin}/api`);

// Derived from API_BASE_URL rather than window.location.origin directly, so
// there's one source of truth for "where the API lives" — Reports.jsx used
// to reimplement this same logic independently (with a different, buggy
// prod fallback of '') to build absolute PDF URLs from relative paths.
export const BACKEND_URL = API_BASE_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
});
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

//Emails
export const getEmailSettings = async (clientId) => {
  const res = await api.get(`/email/settings/${clientId}`);
  return res.data;
};

export const saveEmailSettings = async (clientId, data) => {
  const res = await api.post(`/email/settings/${clientId}`, data);
  return res.data;
};

export const sendTestEmail = async (data) => {
  const res = await api.post('/email/test', data);
  return res.data;
};

export const sendMonthlyReport = async (clientId) => {
  const res = await api.post(`/email/send-monthly/${clientId}`);
  return res.data;
};

export const getGmailConnectionStatus = async () => {
  const res = await api.get('/email/google/status');
  return res.data;
};

export const connectGmail = async (data) => {
  const res = await api.post('/email/google/connect', data);
  return res.data;
};

export const disconnectGmail = async () => {
  const res = await api.delete('/email/google/connect');
  return res.data;
};

export const getEmailSenderReadiness = async () => {
  const res = await api.get('/email/sender-readiness');
  return res.data;
};

// Clients
export const getClients = () => api.get('/clients').then(r => r.data);
export const getClient = (id) => api.get(`/clients/${id}`).then(r => r.data);
export const createClient = (data) => api.post('/clients', data).then(r => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then(r => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then(r => r.data);

// Uploads
export const uploadFile = (formData, onProgress) =>
  api.post('/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => onProgress && onProgress(Math.round((e.loaded * 100) / e.total)),
  }).then(r => r.data);

export const manualEntry = (data) => api.post('/uploads/manual', data).then(r => r.data);
export const getUploads = (clientId) => api.get(`/uploads/client/${clientId}`).then(r => r.data);
export const getUploadStatus = (id) => api.get(`/uploads/${id}/status`).then(r => r.data);
export const confirmUploadDate = (id, dateRangeStart, dateRangeEnd) =>
  api.post(`/uploads/${id}/confirm-date`, { dateRangeStart, dateRangeEnd }).then(r => r.data);

// Performance
export const getSummary = (clientId, params) =>
  api.get(`/performance/summary/${clientId}`, { params }).then(r => r.data);
export const getTrends = (clientId, params) =>
  api.get(`/performance/trends/${clientId}`, { params }).then(r => r.data);
export const getComparison = (clientId, params) =>
  api.get(`/performance/comparison/${clientId}`, { params }).then(r => r.data);
export const getCampaigns = (clientId, params) =>
  api.get(`/performance/campaigns/${clientId}`, { params }).then(r => r.data);
export const getPlatforms = (clientId, params) =>
  api.get(`/performance/platforms/${clientId}`, { params }).then(r => r.data);

// AI Insights
export const generateAIInsights = (clientId) =>
  api.post(`/ai-insights/generate/${clientId}`).then(r => r.data);

export const getAIInsights = (clientId) =>
  api.get(`/ai-insights/${clientId}`).then(r => r.data);

// Reports
export const generateReport = (data) => api.post('/reports/generate', data).then(r => r.data);
export const getReportHistory = async (clientId) => {
  const res = await api.get(`/reports/history/${clientId}`);
  return res.data;
};
export const deleteReport = (reportId) =>
  api.delete(`/reports/${reportId}`).then(r => r.data);

export const getReportTemplates = () => api.get('/reports/templates').then(r => r.data);
export const purchaseTemplateOrder = (templateKey) =>
  api.post(`/reports/templates/${templateKey}/purchase`).then(r => r.data);
export const verifyTemplatePurchase = (templateKey, data) =>
  api.post(`/reports/templates/${templateKey}/purchase/verify`, data).then(r => r.data);

export const createReportCreditOrder = () =>
  api.post('/reports/pay-per-report/create-order').then(r => r.data);
export const verifyReportCreditPayment = (data) =>
  api.post('/reports/pay-per-report/verify', data).then(r => r.data);

export const previewUpload = (formData, onUploadProgress) =>
  api.post('/uploads/preview', formData, {
    onUploadProgress: (e) => {
      if (onUploadProgress && e.total) {
        onUploadProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  }).then(res => res.data);

export const verifyEmail = (token) =>
  api.get('/auth/verify-email', { params: { token } }).then(r => r.data);
export const resendVerification = () =>
  api.post('/auth/resend-verification').then(r => r.data);
export const forgotPassword = (email) =>
  api.post('/auth/forgot-password', { email }).then(r => r.data);
export const resetPassword = (token, newPassword) =>
  api.post('/auth/reset-password', { token, newPassword }).then(r => r.data);

export const confirmUploadMapping = (uploadId, mapping) =>
  api.post(`/uploads/${uploadId}/confirm-mapping`, { mapping })
    .then(res => res.data);
 // Subscription
  export const getSubscription = async () => {
    const res = await api.get('/subscription');
    return res.data;
  };
  export const cancelDowngrade = () =>
    api.put('/subscription/cancel-downgrade').then(r => r.data);
    export const updateSubscriptionPlan = (planName) =>
      api.put('/subscription/plan', { planName }).then(r => r.data);

// Payments
export const createPaymentOrder = (data) =>
  api.post('/payments/create-order', data).then(r => r.data);

export const verifyPayment = (data) =>
  api.post('/payments/verify', data).then(r => r.data);

export const getBillingHistory = () =>
  api.get('/payments/history').then(r => r.data);

export const getActiveCoupons = () =>
  api.get('/payments/coupons/active').then(r => r.data);
// Dashboard
export const getDashboardOverview = () => api.get('/dashboard/overview').then(r => r.data);

export const generateReceipt = (paymentId) =>
  api.get(`/payments/receipt/${paymentId}`).then(r => r.data);

export const getIntegrations = () =>
  api.get('/integrations').then(r => r.data);

export const startMetaConnection = (clientId) =>
  api.post(`/integrations/${clientId}/meta/connect`).then(r => r.data);

export const startGoogleConnection = (clientId) =>
  api.post(`/integrations/${clientId}/google/connect`).then(r => r.data);

export const startLinkedInConnection = (clientId) =>
  api.post(`/integrations/${clientId}/linkedin/connect`).then(r => r.data);

export const startShopifyConnection = (clientId, shop) =>
  api.post(`/integrations/${clientId}/shopify/connect`, { shop }).then(r => r.data);

export const startTikTokConnection = (clientId) =>
  api.post(`/integrations/${clientId}/tiktok/connect`).then(r => r.data);

export const syncIntegration = (id, options) =>
  api.post(`/integrations/${id}/sync`, options || {}).then(r => r.data);

// Triggers the connection's one-time initial backfill import with a chosen
// date range. `range` is one of '30d' | '90d' | 'mtd' | 'custom' (custom
// requires dateFrom/dateTo). Uses the same /sync endpoint as "Sync Now"
// with initial:true, so it shares all the same quota/rate-limit/locking
// server-side logic — an initial import IS a manual, user-triggered sync.
export const triggerInitialImport = (id, { range, dateFrom, dateTo } = {}) =>
  api.post(`/integrations/${id}/sync`, { initial: true, range, dateFrom, dateTo }).then(r => r.data);

export const getOverlapWarnings = (clientId) =>
  api.get('/integrations/overlap-warnings', { params: clientId ? { clientId } : {} }).then(r => r.data);

export const disconnectIntegration = (id) =>
  api.delete(`/integrations/${id}`).then(r => r.data);

export const getIntegrationLogs = (id) =>
  api.get(`/integrations/${id}/logs`).then(r => r.data);

export const getIntegrationQuota = () =>
  api.get('/integrations/quota').then(r => r.data);

export const getIntegrationReadiness = () =>
  api.get('/integrations/readiness').then(r => r.data);

export const updateConnectionSchedule = (id, data) =>
  api.patch(`/integrations/${id}/schedule`, data).then(r => r.data);

export const getPendingAccountSelection = (token) =>
  api.get(`/integrations/pending/${token}`).then(r => r.data);

export const confirmAccountSelection = (token, accountId) =>
  api.post(`/integrations/pending/${token}/confirm`, { accountId }).then(r => r.data);
// Agency
export const getAgency = () => api.get('/agency').then(r => r.data);
export const updateAgency = async (data) => {
  const res = await api.put('/agency', data);
  return res.data;
};

// Super Admin — Pricing & Coupons
export const getPricingPlans = () => api.get('/super-admin/pricing').then(r => r.data);
export const updatePricingPlan = (planKey, data) =>
  api.put(`/super-admin/pricing/${planKey}`, data).then(r => r.data);
export const getCoupons = () => api.get('/super-admin/coupons').then(r => r.data);
export const createCoupon = (data) => api.post('/super-admin/coupons', data).then(r => r.data);
export const updateCoupon = (id, data) => api.put(`/super-admin/coupons/${id}`, data).then(r => r.data);
export const deleteCoupon = (id) => api.delete(`/super-admin/coupons/${id}`).then(r => r.data);

export const getPlatformSettings = () => api.get('/super-admin/settings').then(r => r.data);
export const updatePlatformSettings = (data) => api.put('/super-admin/settings', data).then(r => r.data);
export const getAdminAuditLog = () => api.get('/super-admin/audit-log').then(r => r.data);

// Public pricing (no auth) — used by the landing page
export const getPublicPricing = () => api.get('/pricing/public').then(r => r.data);

export default api;
